import React, {FC, useState} from "react";
import {Box, Button, Popover, Typography} from "@material-ui/core";
import Badge from "@mui/material/Badge";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import {grey} from "@material-ui/core/colors";
import InfiniteScroll from "react-infinite-scroll-component";
import getDatePattern from "tools/formats/getDatePattern";
import {apiV2} from "api/firebase";
import {useSupplierNotificationBar} from "store/notification-bar/supplier-notification-bar.store";
import {useSupplierNotificationBarStatus} from "hooks/useSupplierNotificationBarStatus";

import NotificationList from "view/components/AppBar/components/NotificationsPopup/components/NotificationList";
import NotificationBarLoader from "view/components/AppBar/components/NotificationsPopup/NotificationLoader";
import FlexBox from "view/components/Layout/FlexBox";
import useStyles from "./styles";

interface SupplierNotificationProps {
  supplierCompanyId: string;
  iconColor?: string;
}

const MIN_TO_LOAD = 15;

/**
 * Supplier-Portal counterpart of `Notification` (the Organiser bell). Kept
 * as a separate component — rather than generalizing `Notification` with a
 * "show or supplier company" scope prop — so the already-shipped Organiser
 * bell carries zero risk from this newer Supplier variant. Calls the real
 * `apiV2.notificationBar.*` gateway with `supplierCompanyId` in place of
 * `showId`.
 *
 * Unlike the Organiser bell, this renders unconditionally once a
 * `supplierCompanyId` is known — the Notification Centre is not Show-scoped,
 * so it must be identical on every Supplier page, including ones with no
 * Show selected.
 *
 */
const SupplierNotification: FC<SupplierNotificationProps> = ({
  supplierCompanyId,
  iconColor,
}) => {
  const classes = useStyles();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const dateFormat = getDatePattern(undefined);

  const {
    isNewNotifications,
    isUnreadNotification,
    refetch: refetchNotificationBar,
  } = useSupplierNotificationBarStatus(supplierCompanyId);

  const [inViewNotificationIds, setInViewNotificationIds] = useState<string[]>(
    []
  );

  const {
    data,
    pagination,
    fetch,
    reload,
    loadMore,
    isLoading,
  } = useSupplierNotificationBar();

  const notifications = data[supplierCompanyId] || [];
  const pageInfo = pagination[supplierCompanyId] || {page: 0, total: 0};
  const hasMore = notifications.length < (pageInfo.total || 0);

  const handlePopoverOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);

    if (notifications.length === 0) {
      await fetch({supplierCompanyId, page: 1, limit: MIN_TO_LOAD});
    }
  };

  const handlePopoverClose = async () => {
    setAnchorElement(null);

    if (inViewNotificationIds.length) {
      await apiV2.notificationBar.changeStatus({
        ids: inViewNotificationIds,
        supplierCompanyId,
      });
    }

    reload(supplierCompanyId);
    await refetchNotificationBar();
    setInViewNotificationIds([]);
  };

  const markAllNotificationsAsRead = async () => {
    try {
      await apiV2.notificationBar.markReadAll({supplierCompanyId});
      setInViewNotificationIds([]);
      reload(supplierCompanyId);
      await refetchNotificationBar();
    } catch (e) {
      console.error(e);
    }
  };

  const markOneNotificationAsRead = async (notificationId: string) => {
    await apiV2.notificationBar.markRead({
      ids: [notificationId],
      supplierCompanyId,
    });
    reload(supplierCompanyId);
    await refetchNotificationBar();
  };

  const showEmptyState = !isLoading && notifications.length === 0;

  return (
    <>
      <Badge
        color="error"
        overlap="circular"
        variant="dot"
        invisible={!isUnreadNotification}
        onClick={handlePopoverOpen}
      >
        <NotificationsNoneIcon
          htmlColor={iconColor || grey[500]}
          width="24px"
          height="24px"
        />
      </Badge>
      <Popover
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          className: classes.popoverPaper,
        }}
      >
        <Box>
          <Typography gutterBottom className={classes.notificationsTitle}>
            Notifications
          </Typography>
          <Box className={classes.divider} />

          {showEmptyState ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.noNotification}
              >
                You don’t have any notifications.
              </Typography>
            </Box>
          ) : (
            <InfiniteScroll
              dataLength={notifications.length}
              next={() => loadMore({supplierCompanyId, limit: MIN_TO_LOAD})}
              hasMore={hasMore}
              loader={<NotificationBarLoader />}
              height={400}
              className={classes.scrollContainer}
            >
              <NotificationList
                notifications={notifications}
                isNewNotifications={isNewNotifications}
                markOneNotificationAsRead={markOneNotificationAsRead}
                setInViewNotificationIds={setInViewNotificationIds}
                isLoading={isLoading}
                dateFormat={dateFormat}
              />
            </InfiniteScroll>
          )}

          {!showEmptyState && (
            <Box className={classes.dividerTop}>
              <FlexBox justifyContent="end">
                <Button
                  onClick={markAllNotificationsAsRead}
                  disabled={!isNewNotifications}
                  color="primary"
                  className={classes.markAllAsReadButton}
                >
                  Mark all as read
                </Button>
              </FlexBox>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default SupplierNotification;
